import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, EASE } from "@/components/Reveal";

function Counter({ to, decimals = 0, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const RANGES = ["TODAY", "WEEK", "MONTH", "LAST 5 DAYS", "LAST 10 DAYS", "LAST 20 DAYS", "CUSTOM", "ALL HISTORY"];
const DIMS = ["SESSION", "EVENT", "DIRECTION", "TIMEFRAME", "TIME OF DAY", "VALIDATION", "TRADE OUTCOME"];

const METRICS = [
  { label: "TOTAL CALLS", value: 96, sub: "P.E.E. TRADE CALLS RECORDED", tone: "text-white" },
  { label: "VALIDATION RATE", value: 61.5, decimals: 1, suffix: "%", sub: "CALLS REACHING VALIDATED", tone: "text-neon" },
  { label: "INVALIDATION RATE", value: 25.0, decimals: 1, suffix: "%", sub: "FILTERED BY VALIDATION", tone: "text-risk" },
  { label: "TP1 HIT RATE", value: 68.6, decimals: 1, suffix: "%", sub: "OF VALIDATED TRADES", tone: "text-neon" },
  { label: "TP2 HIT RATE", value: 47.5, decimals: 1, suffix: "%", sub: "OF VALIDATED TRADES", tone: "text-neon" },
  { label: "VALIDATED → SL", value: 18.6, decimals: 1, suffix: "%", sub: "STOPPED BEFORE TP1", tone: "text-risk" },
  { label: "WIN RATE", value: 68.6, decimals: 1, suffix: "%", sub: "VALIDATED TRADES REACHING TP1 OR BETTER", tone: "text-white" },
  { label: "AVG. TRADE DURATION", value: 17, suffix: "m", sub: "ENTRY → RECORDED OUTCOME", tone: "text-white" },
];

const BARS = [
  { label: "VALIDATION — REACHED VALIDATED", pct: 61.5 },
  { label: "OUTCOME — TP1 HIT", pct: 68.6 },
  { label: "OUTCOME — TP2 HIT", pct: 47.5 },
  { label: "OUTCOME — VALIDATED → SL", pct: 18.6 },
];

export default function AnalyticsV2() {
  const [range, setRange] = useState("WEEK");

  return (
    <section id="analytics" data-testid="analytics-section" className="border-t border-line bg-[#070809] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          chapter="06 — Analytics"
          testid="analytics-heading"
          title={
            <>
              Stop guessing.
              <br />
              Start <span className="glow-text-neon text-neon">measuring.</span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist">
            <span className="text-white">
              What gets measured can be improved.
            </span>{" "}
            Traders often say: “I think this setup works.” “I feel like mornings
            are better.” “I usually do better on longs.” But improvement
            shouldn't depend entirely on memory. P.E.E. Analytics transforms the
            P.E.E. trade lifecycle into measurable information.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14 overflow-hidden rounded-xl border border-line bg-charcoal">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4 sm:px-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white">
                P.E.E. Analytics
              </span>
              <span
                data-testid="analytics-demo-badge"
                className="rounded border border-neon/40 bg-neon/5 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-neon"
              >
                Demo Data
              </span>
            </div>

            <div className="space-y-3 border-b border-line px-5 py-4 sm:px-7">
              <div className="flex flex-wrap gap-2">
                {RANGES.map((r) => (
                  <button
                    key={r}
                    data-testid={`analytics-filter-${r.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setRange(r)}
                    className={`rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${
                      range === r
                        ? "border-neon/60 bg-neon/10 text-neon"
                        : "border-line text-dim hover:border-mist/50 hover:text-mist"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {DIMS.map((d) => (
                  <button
                    key={d}
                    data-testid={`analytics-dim-${d.toLowerCase().replace(/ /g, "-")}`}
                    className="flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-mist transition-colors duration-300 hover:border-neon/40 hover:text-white"
                  >
                    {d}
                    <ChevronDown size={11} className="text-dim" />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-px bg-line lg:grid-cols-4">
              {METRICS.map((m, i) => (
                <div
                  key={m.label}
                  data-testid={`analytics-metric-${i}`}
                  className="bg-charcoal p-5 sm:p-6"
                >
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-dim">
                    {m.label}
                  </div>
                  <div className={`mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl ${m.tone}`}>
                    <Counter to={m.value} decimals={m.decimals || 0} suffix={m.suffix || ""} />
                  </div>
                  <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-dim">
                    {m.sub}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line px-5 py-6 sm:px-7">
              <div className="mb-5 font-mono text-[10px] uppercase tracking-[0.25em] text-dim">
                Breakdown — session · event · direction · timeframe · time of day
              </div>
              <div className="space-y-4">
                {BARS.map((b, i) => (
                  <div key={b.label} data-testid={`analytics-bar-${i}`}>
                    <div className="mb-1.5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.14em]">
                      <span className="text-mist">{b.label}</span>
                      <span className="text-neon">{b.pct}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-ink">
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: b.pct / 100 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, delay: 0.1 * i, ease: EASE }}
                        className="h-full origin-left rounded-full bg-gradient-to-r from-neon/40 to-neon"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 font-mono text-[9px] uppercase leading-relaxed tracking-[0.18em] text-dim">
                Demo data shown for illustrative purposes only. Individual
                trading results vary. Past performance does not guarantee future
                results.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-2">
          <Reveal>
            <h3 className="font-display text-3xl font-extrabold uppercase leading-[1.08] tracking-tight text-white sm:text-4xl">
              Don't just trade.
              <br />
              <span className="text-neon">Study your execution.</span>
            </h3>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-display text-2xl font-extrabold uppercase leading-[1.1] tracking-tight text-white sm:text-3xl">
              Measure the process.
              <br />
              Not just the P&amp;L.
            </h3>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-mist">
              P.E.E. Analytics tracks the lifecycle of P.E.E. trades—from
              identification and validation through TP1, TP2, invalidation, and
              stop loss—helping Executors study how the Game Plan performs and
              how consistently they execute it.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
